/**
 * Offline unlock (OPEN_QUESTIONS D5). After a successful online PIN sign-in the iPad stores a
 * salted PBKDF2 hash of the PIN, so the mechanic can unlock while there's no signal. The
 * server still owns the real PIN (Argon2id) and the lockout; offline unlocks are only trusted
 * for syncing because the device is enrolled and the mechanic signed in on it recently.
 */
import { kvGet, kvSet } from './db';

const ITERATIONS = 150_000;
const LOCAL_MAX_FAILS = 5;
const LOCAL_LOCK_MS = 15 * 60_000;

interface Verifier {
  salt: string;
  hash: string;
}
interface LocalLock {
  fails: number;
  until: number;
}

const b64 = (buf: ArrayBuffer | Uint8Array) => btoa(String.fromCharCode(...new Uint8Array(buf)));
const unb64 = (s: string): Uint8Array<ArrayBuffer> => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

async function derive(pin: string, salt: Uint8Array<ArrayBuffer>): Promise<string> {
  const key = await crypto.subtle.importKey('raw', new TextEncoder().encode(pin), 'PBKDF2', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits({ name: 'PBKDF2', hash: 'SHA-256', salt, iterations: ITERATIONS }, key, 256);
  return b64(bits);
}

export async function rememberPin(userId: number, pin: string) {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  await kvSet(`verifier:${userId}`, { salt: b64(salt), hash: await derive(pin, salt) } satisfies Verifier);
  await kvSet(`lock:${userId}`, { fails: 0, until: 0 } satisfies LocalLock);
}

export async function canUnlockOffline(userId: number) {
  return Boolean(await kvGet<Verifier>(`verifier:${userId}`));
}

/** 'ok' | 'wrong' | 'locked' | 'unknown' (never signed in online on this iPad) */
export async function unlockOffline(userId: number, pin: string): Promise<'ok' | 'wrong' | 'locked' | 'unknown'> {
  const v = await kvGet<Verifier>(`verifier:${userId}`);
  if (!v) return 'unknown';
  const lock = (await kvGet<LocalLock>(`lock:${userId}`)) ?? { fails: 0, until: 0 };
  if (lock.until > Date.now()) return 'locked';
  if ((await derive(pin, unb64(v.salt))) === v.hash) {
    await kvSet(`lock:${userId}`, { fails: 0, until: 0 });
    return 'ok';
  }
  const fails = lock.fails + 1;
  await kvSet(`lock:${userId}`, fails >= LOCAL_MAX_FAILS ? { fails: 0, until: Date.now() + LOCAL_LOCK_MS } : { fails, until: 0 });
  return fails >= LOCAL_MAX_FAILS ? 'locked' : 'wrong';
}
